import { Layout } from "providers/Layout";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Show
} from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Card } from "shared/components/Card";
import { Plus } from "untitledui-js-base";
import { AddIntegrationModal } from "views/settings-integration/components/AddIntegrationModal";
import { useSettingsController } from "views/settings-integration/controller";
import { PMSNames } from "./integrations/pms/constants";

interface IntegrationsItem {
  enabled: boolean;
  title: string;
  description: string;
  link: string;
}

export default function IntegrationsView() {
  const [showAddIntegrationModal, setShowAddIntegrationModal] = useState(false);
  const navigate = useNavigate();
  const { propertyManagementSystem, currentSettings } = useSettingsController();

  const integrations: IntegrationsItem[] = useMemo(() => {
    const pmsTitle = propertyManagementSystem
      ? PMSNames[propertyManagementSystem]
      : "PMS"; //this is fallback which should not happen, added just to be safe

    return [
      {
        enabled: Boolean(currentSettings.google.googleAnalyticsId),
        title: "Google Analytics",
        description: "Integration between Boom and Google Analytics",
        link: "/settings/integration/google-analytics"
      },
      {
        enabled: Boolean(propertyManagementSystem),
        title: pmsTitle,
        description: `Integration between Boom and ${pmsTitle}`,
        link: "/settings/integration/pms"
      }
    ];
  }, [propertyManagementSystem, currentSettings]);

  return (
    <Layout
      header={
        <>
          <div className="flex items-center justify-between border-b border-alpha-40 px-3 md:px-6 pb-3">
            <BreadCrumbs
              crumbs={[
                {
                  title: "Settings",
                  href: "/settings"
                },
                {
                  title: "Integration",
                  href: "/settings/integration"
                }
              ]}
            />
          </div>
          <div className="flex items-center justify-between p-6 pb-3">
            <h1 className="text-xl font-bold text-content-1000">Integration</h1>
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              Icon={Plus}
              onClick={() => setShowAddIntegrationModal(true)}
            >
              Add integration
            </Button>
          </div>
        </>
      }
    >
      <div className="flex flex-wrap gap-4">
        {integrations.map((integration) => {
          if (!integration.enabled) return null;

          return (
            <Card
              className="flex flex-col p-8 w-[368px]"
              key={integration.title}
            >
              <div>
                <Badge variant={BadgeVariant.SUCCESS} size={BadgeSize.SM}>
                  Connected
                </Badge>
              </div>

              <div className="mt-6 mb-4 h-full">
                <div className="py-2.5 space-y-2">
                  <p className="font-bold">{integration.title}</p>
                  <p className="text-sm text-content-400">
                    {integration.description}
                  </p>
                </div>
              </div>

              <div className="flex border-t border-alpha-100 pt-4 items-end">
                <Button
                  variant={ButtonVariant.TEXT}
                  size={ButtonSize.SM}
                  onClick={() => navigate(integration.link)}
                >
                  Configure
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* We need this show to drop all states in modal when it closes */}
      <Show if={showAddIntegrationModal}>
        <AddIntegrationModal
          opened={showAddIntegrationModal}
          onClose={() => setShowAddIntegrationModal(false)}
        />
      </Show>
    </Layout>
  );
}
